/**
 * Categories hooks for React Query
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { categoriesService } from '@/services/categories';
import type { Category, CategoryType } from '@/types';
import { useMemo } from 'react';

const CATEGORIES_KEY = 'categories';

/**
 * Options for useCategories hook
 */
export interface UseCategoriesOptions {
  /**
   * Filter categories by type (income, expense, transfer).
   * If undefined, returns all categories.
   */
  type?: CategoryType;
}

/**
 * Hook for fetching categories with optional type filtering
 *
 * @param options - Optional filtering options
 * @returns React Query result with categories array
 *
 * @example
 * // Get all categories
 * const { data: allCategories } = useCategories();
 *
 * // Get only income categories
 * const { data: incomeCategories } = useCategories({ type: 'income' });
 *
 * // Get only expense categories
 * const { data: expenseCategories } = useCategories({ type: 'expense' });
 */
export function useCategories(options: UseCategoriesOptions = {}) {
  const { type } = options;

  return useQuery<Category[], Error>({
    // Include type in cache key for proper cache separation
    // ['categories'] for all, ['categories', 'income'] for income-only, etc.
    queryKey: type ? [CATEGORIES_KEY, type] : [CATEGORIES_KEY],
    queryFn: () => categoriesService.getAll({ type, includeSystem: true }),
    staleTime: 1000 * 60 * 30, // Categories rarely change, cache for 30 minutes
  });
}

/**
 * Hook for searching categories
 * Note: Uses getAll and filters client-side since categories are cached
 */
export function useCategorySearch(query: string) {
  const { data: allCategories } = useCategories();

  return useQuery<Category[], Error>({
    queryKey: [CATEGORIES_KEY, 'search', query],
    queryFn: () => {
      if (!allCategories) return [];
      const lowerQuery = query.toLowerCase();
      return allCategories.filter(
        (cat) =>
          cat.name?.toLowerCase()?.includes(lowerQuery) ||
          cat.path?.toLowerCase()?.includes(lowerQuery)
      );
    },
    enabled: query.length >= 2 && !!allCategories,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get category by ID from cached data
 */
export function useCategoryById(categoryId: string | undefined) {
  const { data: categories } = useCategories();

  return useMemo(() => {
    if (!categoryId || !categories) return undefined;
    return categories.find((cat) => cat.id === categoryId);
  }, [categoryId, categories]);
}

/**
 * Build a hierarchical tree from flat categories
 */
export interface CategoryTreeNode extends Category {
  children: CategoryTreeNode[];
}

export function buildCategoryTree(categories: Category[]): CategoryTreeNode[] {
  const map = new Map<string, CategoryTreeNode>();
  const roots: CategoryTreeNode[] = [];

  // First pass: create all nodes with empty children arrays
  categories.forEach((cat) => {
    map.set(cat.id, { ...cat, children: [] });
  });

  // Second pass: build the tree
  categories.forEach((cat) => {
    const node = map.get(cat.id)!;
    if (cat.parentId && map.has(cat.parentId)) {
      map.get(cat.parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

/**
 * Hook to get category tree
 */
export function useCategoryTree() {
  const { data: categories, ...rest } = useCategories();

  const tree = useMemo(() => {
    if (!categories) return [];
    return buildCategoryTree(categories);
  }, [categories]);

  return { data: tree, ...rest };
}

/**
 * Prefetch categories for better UX
 * This hook automatically prefetches categories on mount
 */
export function usePrefetchCategories() {
  const queryClient = useQueryClient();

  // Prefetch immediately on hook call (in useEffect-like manner via React Query)
  queryClient.prefetchQuery({
    queryKey: [CATEGORIES_KEY],
    queryFn: () => categoriesService.getAll(),
    staleTime: 1000 * 60 * 30,
  });
}

export default useCategories;
